import { useState } from 'react';
import { Box, Card, CardContent, Container, Drawer, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Breadcrumb } from './Breadcrumb';
import { Footer } from './Footer';

/**
 * Application Layout
 *
 * Responsive layout with:
 * - Fixed header with app name, language selector, user info
 * - Responsive sidebar (permanent on desktop, temporary on mobile)
 *   - Module selector dropdown
 *   - Module-aware navigation
 * - Breadcrumb navigation
 * - Main content area with proper spacing
 * - Footer with copyright and version info
 *
 * Breakpoints:
 * - Mobile (< 900px): Temporary drawer with hamburger menu
 * - Desktop (≥ 900px): Permanent drawer
 */

const DRAWER_WIDTH = 260;

export const AppLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        // Prevent horizontal overflow at the root level
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
    >
      {/* Header - Fixed at top */}
      <Header onMenuClick={handleDrawerToggle} />

      {/* Sidebar - Responsive */}
      <Box
        component="nav"
        sx={{
          width: { md: DRAWER_WIDTH },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer (temporary) */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                bgcolor: 'background.paper',
              },
            }}
          >
            <Toolbar /> {/* Spacer for header */}
            <Sidebar onItemClick={handleDrawerToggle} />
          </Drawer>
        ) : (
          // Desktop drawer (permanent)
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                bgcolor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
            }}
            open
          >
            <Toolbar /> {/* Spacer for header */}
            <Sidebar />
          </Drawer>
        )}
      </Box>

      {/* Main content area - includes content + footer */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          // Properly constrain width to prevent overflow
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0, // Allow flex item to shrink below content size
          minHeight: '100vh',
          overflowX: 'hidden',
        }}
      >
        <Toolbar /> {/* Spacer for fixed header */}

        {/* Content container - grows to push footer down */}
        <Container
          maxWidth="lg"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
          }}
        >
          {/* Breadcrumb navigation - above the card */}
          <Breadcrumb />

          {/* Content Card */}
          <Card
            elevation={1}
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              mt: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                p: { xs: 2, sm: 3 },
                '&:last-child': { pb: { xs: 2, sm: 3 } },
                // Tables and other wide content will scroll within this container
                '& .MuiDataGrid-root, & table, & .table-container': {
                  maxWidth: '100%',
                  overflowX: 'auto',
                },
              }}
            >
              <Outlet />
            </CardContent>
          </Card>
        </Container>

        {/* Footer - inside main area, follows the card */}
        <Footer />
      </Box>
    </Box>
  );
};
